import { Component } from 'react'
import CarFileLogo from './CarFileLogo.jsx'

// Error boundary wrapping the /login and /signup routes. Without this, an
// uncaught throw inside the Auth0 SDK (e.g. transaction-manager state
// corruption — the original "Cannot read properties of undefined (reading
// 'payload')" symptom) crashes the React tree and leaves the user staring at
// a white screen with no recovery path.
//
// We catch the error, log it for debugging, and render a friendly fallback
// with a Try Again button (full reload) and a contact-support link.
class AuthErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // Surface the original exception so it shows in browser devtools and any
    // wired-up error tracker (Sentry / GA / etc) can pick it up.
    console.error('[AuthErrorBoundary] Auth flow crashed:', error, errorInfo)
  }

  handleRetry = () => {
    // Full reload clears in-memory React state and any half-initialised SDK
    // state. Cheaper and more reliable than trying to selectively reset.
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-600 to-brand-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <CarFileLogo size="xl" className="dark:hidden" />
            <CarFileLogo size="xl" className="hidden dark:block" dark />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Something went wrong</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            We couldn&apos;t complete the sign-in step. Please try again. If the
            problem persists, contact us at{' '}
            <a href="mailto:support@carfile.app" className="underline hover:text-brand-500">
              support@carfile.app
            </a>.
          </p>
          <button
            onClick={this.handleRetry}
            className="w-full py-3 px-6 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-medium transition-colors text-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }
}

export default AuthErrorBoundary
